// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@guestsupply/App/Components/Qty/QtyField";
import { color, xMask, xScreen } from "@guestsupply/XDL/Utilities";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Change
    showMultipleSaleQty?: boolean;
    showActions?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    qtyFieldStyles?: QtyFieldStyles;
    qtyFieldWrapper?: InjectableCss;
    controlsWrapper?: InjectableCss;
    qtyClickable?: ClickablePresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                text-align: center;
            `,
        },
    },
    qtyFieldWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
        `,
    },
    controlsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    qtyClickable: {
        css: css`
            width: 18px;
            height: 17.5px;
            padding: 1.5px 4px;
            background-color: ${color("text")};
            &.plus {
                border-radius: 0 4px 0 0;
            }
            &.minus {
                border-radius: 0 0 4px 0;
            }

            &:hover {
                opacity: 0.8;
            }

            &:active {
                background: ${color("common.ui6")};
            }
        `,
    },
    readOnlyQuantityText: {
        text: {
            size: 14,
            css: css`
                font-size: 16px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        heading: {
            weight: 400,
            size: 12,
            transform: "capitalize",
            css: css`
                margin-bottom: 12px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    showActions = true,
    showMultipleSaleQty = true,
}) => {
    const minQty = cartLine.properties?.multipleSaleQty || 1;

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    // XNG-Changes: GSS-876
    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // XNG-Changes: GSS-876
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (value && Number(xMask.qty(value)) === 0) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        blurHandler();
    };
    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));
    if (editable && !isOutOfStock(cartLine)) {
        // XNG-CHANGES : Changing Textfield to QtyField
        return (
            <QtyField
                disabled={cartLine.isPromotionItem}
                label={label ?? translate("QTY")}
                value={value}
                type="number"
                min={minQty}
                showMultipleSaleQty={
                    showMultipleSaleQty && cartLine.properties?.multipleSaleQty
                        ? Number(cartLine.properties?.multipleSaleQty) > 1
                        : false
                }
                max={9999}
                data-test-selector="product_qtyOrdered"
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                onClick={event => event.currentTarget.focus()}
                showActionButton={showActions}
                roundBy={
                    showMultipleSaleQty && cartLine.properties?.multipleSaleQty
                        ? Number(cartLine.properties?.multipleSaleQty)
                        : 1
                }
                extendedStyles={styles.qtyFieldStyles}
            />
        );
    }

    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyQuantityText}
            heading={label || translate("QTY")}
            text={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
